import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import {
  CircularProgress,
  Box,
  Button,
  TextField,
  Autocomplete,
  Grid,
  Typography,
  Stack,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import URLs from "constants/urls";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import Cookie from "js-cookie";
import { StyledButton } from "components/StyledComponents/styledComponents";

export default function BusinessBreakdownDialog({
  open,
  onClose,
  startDate,
  endDate,
  dsaId,
  user,
  setAlert,
  isIncomeByMis = false,
  urlApi,
  selectedDsaId,
}) {
  const gridRef = useRef(null);

  const [rowData, setRowData] = useState([]);
  const [filteredRowData, setFilteredRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pinnedBottomRowData, setPinnedBottomRowData] = useState([]);
  const [filters, setFilters] = useState([{ column: null, text: "" }]);
  const [columnOptions, setColumnOptions] = useState([]);

  const jwt_token = Cookie.get("jwt_token");

  const formatIndianCurrency = (value) => {
    if (value == null) return "";
    return Number(value).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const autoSizeColumns = useCallback(() => {
    if (!gridRef.current?.api) return;
    const allColIds = gridRef.current.api.getAllGridColumns()?.map((col) => col.getId());
    if (allColIds?.length) {
      gridRef.current.api.autoSizeColumns(allColIds, false);
    }
  }, []);

  const showDsaColumn = selectedDsaId !== 0;

  const columnDefs = useMemo(() => {
    const baseColumns = [
      {
        headerName: "Application Number",
        field: "applicationNumber",
        sortable: true,
        filter: true,
        minWidth: 170,
      },
      {
        headerName: "Business Type",
        field: "businessType",
        sortable: true,
        filter: true,
        minWidth: 140,
      },
      {
        headerName: "Bank Name",
        field: "bankerName",
        sortable: true,
        filter: true,
        minWidth: 140,
      },
      {
        headerName: "Customer Name",
        field: "customerName",
        sortable: true,
        filter: true,
        minWidth: 150,
      },
      {
        headerName: "Provider Name",
        field: "providerName",
        sortable: true,
        filter: true,
        minWidth: 150,
      },
    ];

    if (showDsaColumn) {
      baseColumns.push({
        headerName: "DSA Name",
        field: "dsaName",
        sortable: true,
        filter: true,
        minWidth: 140,
        valueGetter: (params) => params.data?.dsaName || "",
      });
    }

    baseColumns.push(
      {
        headerName: "Applied Amount",
        field: "appliedAmount",
        sortable: true,
        filter: true,
        minWidth: 150,
        headerClass: "ag-right-aligned-header",
        cellStyle: (params) => ({
          fontWeight: params.node?.rowPinned === "bottom" ? "bold" : "500",
          textAlign: "right",
        }),
        valueFormatter: (params) => formatIndianCurrency(params.value),
        aggFunc: "sum",
        type: "numericColumn",
      },
      {
        headerName: "Actual Revenue",
        field: "actualRevenue",
        sortable: true,
        filter: true,
        minWidth: 150,
        headerClass: "ag-right-aligned-header",
        cellStyle: (params) => ({
          fontWeight: params.node?.rowPinned === "bottom" ? "bold" : "500",
          textAlign: "right",
        }),
        valueFormatter: (params) => formatIndianCurrency(params.value),
        aggFunc: "sum",
        type: "numericColumn",
      },
      {
        headerName: "Disbursed Amount",
        field: "disbursedAmount",
        sortable: true,
        filter: true,
        minWidth: 160,
        headerClass: "ag-right-aligned-header",
        cellStyle: (params) => ({
          fontWeight: params.node?.rowPinned === "bottom" ? "bold" : "500",
          textAlign: "right",
        }),
        valueFormatter: (params) => formatIndianCurrency(params.value),
        aggFunc: "sum",
        type: "numericColumn",
      },
      {
        headerName: "DMA Payout",
        field: "dmaPayout",
        sortable: true,
        filter: true,
        minWidth: 140,
        headerClass: "ag-right-aligned-header",
        cellStyle: (params) => ({
          fontWeight: params.node?.rowPinned === "bottom" ? "bold" : "500",
          textAlign: "right",
        }),
        valueFormatter: (params) => formatIndianCurrency(params.value),
        aggFunc: "sum",
        type: "numericColumn",
      },
      {
        headerName: "MIS Revenue",
        field: "amount",
        sortable: true,
        filter: true,
        minWidth: 140,
        headerClass: "ag-right-aligned-header",
        cellStyle: (params) => ({
          fontWeight: params.node?.rowPinned === "bottom" ? "bold" : "500",
          textAlign: "right",
        }),
        valueFormatter: (params) => formatIndianCurrency(params.value),
        aggFunc: "sum",
        type: "numericColumn",
      },
      {
        headerName: "Business Status",
        field: "businessStatusLabel",
        sortable: true,
        filter: true,
        minWidth: 150,
      },
      {
        headerName: "Status Label",
        field: "statusLabel",
        sortable: true,
        filter: true,
        minWidth: 130,
      }
    );

    return baseColumns;
  }, [showDsaColumn]);

  useEffect(() => {
    const options = columnDefs
      .filter((col) => col.field)
      .map((col) => ({ label: col.headerName, value: col.field, field: col.field }));
    setColumnOptions(options);
  }, [columnDefs]);

  const activeFilters = filters.filter((f) => f.column && f.text.trim() !== "");

  useEffect(() => {
    if (activeFilters.length === 0) {
      setFilteredRowData(rowData);
      return;
    }
    const filtered = rowData.filter((row) =>
      activeFilters.every(({ column, text }) => {
        const val = row[column.field];
        if (val == null) return false;
        const lowerText = text.toLowerCase();
        if (typeof val === "number") return val.toString().includes(text);
        return val.toString().toLowerCase().includes(lowerText);
      })
    );
    setFilteredRowData(filtered);
  }, [rowData, filters]);

  const calculateFooterTotals = useCallback((data) => {
    if (!data || data.length === 0)
      return { disbursedAmount: 0, appliedAmount: 0, amount: 0, actualRevenue: 0, dmaPayout: 0 };
    return {
      disbursedAmount: data.reduce((sum, row) => sum + (row.disbursedAmount || 0), 0),
      appliedAmount: data.reduce((sum, row) => sum + (row.appliedAmount || 0), 0),
      amount: data.reduce((sum, row) => sum + (row.amount || 0), 0),
      actualRevenue: data.reduce((sum, row) => sum + (row.actualRevenue || 0), 0),
      dmaPayout: data.reduce((sum, row) => sum + (row.dmaPayout || 0), 0),
    };
  }, []);

  useEffect(() => {
    const dataToUse = activeFilters.length > 0 ? filteredRowData : rowData;
    const totals = calculateFooterTotals(dataToUse);
    setPinnedBottomRowData([
      {
        businessType: "Total",
        applicationNumber: "",
        businessStatusLabel: "",
        statusLabel: "",
        customerName: "",
        providerName: "",
        dsaName: "",
        disbursedAmount: totals.disbursedAmount,
        appliedAmount: totals.appliedAmount,
        actualRevenue: totals.actualRevenue,
        amount: totals.amount,
        dmaPayout: totals.dmaPayout,
      },
    ]);
  }, [rowData, filteredRowData, filters, calculateFooterTotals]);

  const handleColumnChange = (index, newColumn) => {
    setFilters((prev) => {
      const updated = [...prev];
      updated[index] = { column: newColumn, text: "" };
      return updated;
    });
  };

  const handleTextChange = (index, value) => {
    setFilters((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], text: value };
      return updated;
    });
  };

  const handleAddFilter = () => {
    setFilters((prev) => [...prev, { column: null, text: "" }]);
  };

  const handleRemoveFilter = (index) => {
    setFilters((prev) => {
      if (prev.length === 1) return [{ column: null, text: "" }];
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleClearAll = () => {
    setFilters([{ column: null, text: "" }]);
  };

  const usedFields = filters.map((f) => f.column?.field).filter(Boolean);

  const handleDownloadExcel = () => {
    let dataToExport = [];
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.forEachNodeAfterFilter((node) => {
        if (node.data) dataToExport.push(node.data);
      });
    } else {
      dataToExport = filteredRowData;
    }
    if (!dataToExport || dataToExport.length === 0) return;

    const filteredTotals = calculateFooterTotals(dataToExport);
    const allData = [
      ...dataToExport,
      {
        businessType: "Total",
        bankerName: "",
        applicationNumber: "",
        customerName: "",
        providerName: "",
        dsaName: "",
        businessStatusLabel: "",
        statusLabel: "",
        disbursedAmount: filteredTotals.disbursedAmount,
        amount: filteredTotals.amount,
        dmaPayout: filteredTotals.dmaPayout,
      },
    ];

    const exportData = allData.map((row) => ({
      "Application Number": row.applicationNumber || "",
      "Applied Amount": row.appliedAmount || 0,
      "Actual Revenue": row.actualRevenue || 0,
      "Business Type": row.businessType || "",
      "Bank Name": row.bankerName || "",
      "Business Status": row.businessStatusLabel || "",
      "Customer Name": row.customerName || "",
      "DMA Payout": row.dmaPayout || 0,
      "Disbursed Amount": row.disbursedAmount || 0,
      "MIS Revenue": row.amount || 0,
      "Provider Name": row.providerName || "",
      ...(showDsaColumn ? { "DSA Name": row.dsaName || "" } : {}),
      "Status Label": row.statusLabel || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Business Breakdown");
    const maxWidths = [];
    exportData.forEach((row) => {
      Object.values(row).forEach((val, i) => {
        const len = val ? val.toString().length : 10;
        maxWidths[i] = Math.max(maxWidths[i] || 10, len);
      });
    });
    worksheet["!cols"] = maxWidths.map((w) => ({ wch: w + 2 }));
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      `Business_Breakdown_${startDate}_to_${endDate}.xlsx`
    );
  };

  useEffect(() => {
    if (!open) return;
    const fetchData = async () => {
      setLoading(true);
      if (!user) {
        setAlert("Please login again", "error");
        setLoading(false);
        return;
      }

      let url = "";
      if (isIncomeByMis) {
        url =
          selectedDsaId === 0
            ? `${urlApi}?fromDate=${startDate}&toDate=${endDate}&assignCompanyId=${selectedDsaId}`
            : `${urlApi}?fromDate=${startDate}&toDate=${endDate}&companyId=${selectedDsaId}`;
      }

      try {
        const response = await fetch(url, {
          method: "GET",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt_token}` },
        });
        if (!response.ok) throw new Error("Network response was not ok");
        const data = await response.json();
        const processedData = data.map((row) => ({ ...row, dsaName: row.dsaName || "" }));
        setRowData(processedData);
        setFilteredRowData(processedData);
      } catch (error) {
        console.error("Error fetching business breakdown:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [
    open,
    startDate,
    endDate,
    dsaId,
    jwt_token,
    user,
    isIncomeByMis,
    urlApi,
    selectedDsaId,
    setAlert,
  ]);

  const displayData = activeFilters.length > 0 ? filteredRowData : rowData;

  if (!open) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "#f4f6f8",
        zIndex: 1300,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* ── Top Bar ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 1.5,
          backgroundColor: "#fff",
          borderBottom: "1px solid #e0e0e0",
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          flexShrink: 0,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onClose}
          variant="outlined"
          size="small"
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
        >
          Back
        </Button>

        <Typography variant="h6" fontWeight={700} color="#1F2937">
          Business Breakdown
        </Typography>

        <StyledButton
          variant="contained"
          color="primary"
          onClick={handleDownloadExcel}
          disabled={rowData.length === 0}
          sx={{ textTransform: "none" }}
        >
          Download Excel
        </StyledButton>
      </Box>

      {/* ── Filter Section ── */}
      <Box sx={{ px: 3, pt: 2, pb: 1, flexShrink: 0 }}>
        <Box
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            p: 2,
            backgroundColor: "#fff",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1.5 }}
          >
            <Typography variant="subtitle2" fontWeight={600} color="#333">
              Filters
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                onClick={handleAddFilter}
                disabled={filters.length >= columnOptions.length}
                sx={{ textTransform: "none", fontSize: 12 }}
              >
                + Add Filter
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={handleClearAll}
                disabled={filters.length === 1 && !filters[0].column && !filters[0].text}
                sx={{ textTransform: "none", fontSize: 12 }}
              >
                Clear All
              </Button>
            </Stack>
          </Stack>

          {filters.map((filter, index) => (
            <Grid
              container
              spacing={1.5}
              alignItems="center"
              key={index}
              sx={{ mb: index < filters.length - 1 ? 1.2 : 0 }}
            >
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={columnOptions.filter(
                    (opt) => !usedFields.includes(opt.field) || opt.field === filter.column?.field
                  )}
                  getOptionLabel={(option) => option.label}
                  value={filter.column}
                  onChange={(_, newVal) => handleColumnChange(index, newVal)}
                  isOptionEqualToValue={(opt, val) => opt.value === val.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`Column ${index + 1}`}
                      variant="outlined"
                      size="small"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={7}>
                <TextField
                  fullWidth
                  label={filter.column ? `Search in "${filter.column.label}"` : "Search value"}
                  variant="outlined"
                  size="small"
                  value={filter.text}
                  onChange={(e) => handleTextChange(index, e.target.value)}
                  disabled={!filter.column}
                  placeholder={
                    filter.column
                      ? `Type to filter ${filter.column.label}...`
                      : "Select a column first"
                  }
                />
              </Grid>
              <Grid item xs={12} sm={1} sx={{ display: "flex", justifyContent: "center" }}>
                <IconButton
                  size="small"
                  onClick={() => handleRemoveFilter(index)}
                  title="Remove filter"
                  sx={{ color: "#d32f2f" }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>
          ))}

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.2, display: "block" }}>
            {activeFilters.length > 0 ? (
              <>
                Showing <strong>{filteredRowData.length}</strong> of{" "}
                <strong>{rowData.length}</strong> rows &nbsp;|&nbsp;
                <span style={{ color: "#1976d2" }}>
                  {activeFilters.map((f) => `${f.column.label} = "${f.text}"`).join("  AND  ")}
                </span>
              </>
            ) : (
              <>
                Total <strong>{rowData.length}</strong> rows
              </>
            )}
          </Typography>
        </Box>
      </Box>

      {/* ── AG Grid ── */}
      <Box sx={{ flex: 1, px: 3, pb: 3, overflow: "hidden" }}>
        {loading ? (
          <Box
            sx={{ height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <div className="ag-theme-alpine" style={{ height: "100%", width: "100%" }}>
            <AgGridReact
              ref={gridRef}
              rowData={displayData}
              columnDefs={columnDefs}
              pinnedBottomRowData={pinnedBottomRowData}
              defaultColDef={{
                resizable: true,
                sortable: true,
                filter: true,
                minWidth: 120,

                wrapHeaderText: true,
                autoHeaderHeight: true,
              }}
              onGridReady={(params) => {
                const allColIds = params.api.getAllGridColumns()?.map((col) => col.getId());
                if (allColIds?.length) {
                  params.api.autoSizeColumns(allColIds, false);
                }
              }}
              onFirstDataRendered={(params) => {
                const allColIds = params.api.getAllGridColumns()?.map((col) => col.getId());
                if (allColIds?.length) {
                  params.api.autoSizeColumns(allColIds, false);
                }
              }}
              animateRows
              pagination
              paginationPageSize={100}
              suppressAggFuncInHeader
              suppressHorizontalScroll={false}
              enableCellTextSelection={true}
              ensureDomOrder={true}
              getRowStyle={(params) => {
                if (params.node.rowPinned === "bottom") {
                  return { fontWeight: "bold", backgroundColor: "#f8f6f5" };
                }
                return null;
              }}
              statusBar={{
                statusPanels: [
                  { statusPanel: "agTotalRowCountComponent", align: "left" },
                  { statusPanel: "agAggregationComponent", align: "right" },
                ],
              }}
            />
          </div>
        )}
      </Box>
    </Box>
  );
}

BusinessBreakdownDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  dsaId: PropTypes.number.isRequired,
  user: PropTypes.object.isRequired,
  setAlert: PropTypes.func.isRequired,
  isIncomeByMis: PropTypes.bool,
  urlApi: PropTypes.string.isRequired,
  selectedDsaId: PropTypes.number,
};
